import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useLang, LANGS } from '../i18n/translations';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Globe, Menu, X, User, LayoutDashboard, LogOut, ChevronDown } from 'lucide-react';

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const links = [
    { to: '/', label: t('home'), end: true },
    { to: '/ponuda', label: t('offer') },
    { to: '/mapa', label: t('map') },
    { to: '/kalendar', label: t('calendar') },
    { to: '/obavijesti', label: t('notices') },
    { to: '/kontakt', label: t('contact') }
  ];

  const navCls = ({ isActive }) =>
    `text-sm whitespace-nowrap transition-colors duration-200 hover:text-[#001752] ${isActive ? 'text-[#001752] font-semibold' : 'text-slate-600'}`;

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
            <img src="/logo-mp.png" alt="Ministarstvo privrede SBK/KSB" className="h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-5 mx-6">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={navCls} data-testid={`nav-link-${l.to.replace('/', '') || 'home'}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-600 gap-1.5" data-testid="lang-switcher">
                  <Globe className="h-4 w-4" /> {LANGS.find((l) => l.code === lang)?.label}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {LANGS.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}
                    className={lang === l.code ? 'font-semibold text-[#001752]' : ''}>
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {!user ? (
              <>
                <Button onClick={() => navigate('/registracija')} data-testid="nav-register-btn"
                  className="bg-[#001752] hover:bg-[#0a2a7a] text-white rounded-md px-5 transition-colors duration-200">
                  {t('register')}
                </Button>
                <Button onClick={() => navigate('/prijava')} data-testid="nav-login-btn" variant="outline"
                  className="border-[#001752] text-[#001752] hover:bg-[#001752] hover:text-white rounded-md px-5 transition-colors duration-200">
                  {t('login')}
                </Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-[#001752] text-[#001752] gap-2 rounded-md" data-testid="nav-user-menu">
                    <User className="h-4 w-4" /> {user.name?.split(' ')[0]}
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-slate-800">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {user.role !== 'turista' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-link">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> {t('adminPanel')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} data-testid="nav-logout">
                    <LogOut className="h-4 w-4 mr-2" /> {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <button className="lg:hidden p-2 text-[#001752]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-4 space-y-3" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setMobileOpen(false)}
              className="block text-slate-700 py-1.5 text-[15px]">
              {l.label}
            </NavLink>
          ))}
          <div className="flex flex-wrap gap-2 pt-2">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)}
                className={`px-3 py-1 rounded-md text-sm border ${lang === l.code ? 'bg-[#001752] text-white border-[#001752]' : 'border-slate-200 text-slate-600'}`}>
                {l.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            {!user ? (
              <>
                <Button onClick={() => { navigate('/registracija'); setMobileOpen(false); }} className="bg-[#001752] text-white flex-1">{t('register')}</Button>
                <Button onClick={() => { navigate('/prijava'); setMobileOpen(false); }} variant="outline" className="border-[#001752] text-[#001752] flex-1">{t('login')}</Button>
              </>
            ) : (
              <>
                {user.role !== 'turista' && (
                  <Button onClick={() => { navigate('/admin'); setMobileOpen(false); }} className="bg-[#001752] text-white flex-1">{t('adminPanel')}</Button>
                )}
                <Button onClick={() => { logout(); setMobileOpen(false); }} variant="outline" className="border-[#001752] text-[#001752] flex-1">{t('logout')}</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
